import { useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import NotificationComposeFields, {
  type NotificationComposeHandle,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/NotificationComposeFields';
import type { HwcgIncidentSpecificsState } from './hwcgIncidentSpecifics';
import {
  hwcgEocNotifyScript,
  stripHwcgGroupReplyPrompts,
  type HwcgEocChannel,
  type HwcgEocNoticeState,
} from './hwcgEocNotice';
import {
  hwcgChannelsFromNotificationTypes,
  hwcgChannelsToNotificationTypes,
  hwcgEditedNoticeScript,
  hwcgNotifyContactLabel,
  type HwcgNotifyListContact,
} from './hwcgGroupNotifyComposer';

export function HwcgGroupNotifyComposeModal({
  open,
  onOpenChange,
  groupLabel,
  notice,
  specifics,
  contacts,
  incidentId,
  pending = false,
  onSend,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupLabel: string;
  notice: HwcgEocNoticeState;
  specifics: HwcgIncidentSpecificsState;
  contacts: HwcgNotifyListContact[];
  incidentId?: string;
  pending?: boolean;
  onSend: (input: {
    contactIds: string[];
    noticeScript: string;
    channels: HwcgEocChannel[];
  }) => void;
}) {
  const composeRef = useRef<NotificationComposeHandle>(null);
  const [deliveryMode, setDeliveryMode] = useState<'now' | 'schedule'>('now');
  const script = stripHwcgGroupReplyPrompts(
    notice.notifyScript ?? hwcgEocNotifyScript(specifics)
  );
  const seed = useMemo(
    () => ({
      acknowledgementRequired: true,
      emailSubject: `${groupLabel} notification`,
      appMessage: script,
      emailMessage: script,
      smsMessage: script,
      phoneMessage: script,
      teamsMessage: script,
      notificationTypes: hwcgChannelsToNotificationTypes(notice.channels),
    }),
    [groupLabel, notice.channels, script]
  );
  const recipients = contacts.map((contact) => ({
    id: contact.id,
    label: hwcgNotifyContactLabel(contact),
  }));

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const collected = composeRef.current?.collect();
    if (!collected || collected.status !== 'ready') return;
    const noticeScript = hwcgEditedNoticeScript(script, collected.fields);
    if (!noticeScript) return;
    onSend({
      contactIds: collected.contactIds ?? [],
      noticeScript,
      channels: hwcgChannelsFromNotificationTypes(
        collected.fields.notificationTypes ?? [],
        notice.channels
      ),
    });
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Create Notification"
      widthClassName="tw:!max-w-5xl tw:sm:!max-w-5xl"
      formProps={{ onSubmit }}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button type="submit" disabled={pending}>
            {deliveryMode === 'schedule' ? 'Schedule' : 'Send'}
          </Button>
        </>
      }
    >
      {open ? (
        <NotificationComposeFields
          key={`${groupLabel}-${script}`}
          ref={composeRef}
          seed={seed}
          allowSchedule={false}
          incidentId={incidentId}
          contactListRecipients={recipients}
          onDeliveryMode={setDeliveryMode}
        />
      ) : null}
    </FormModal>
  );
}
